import { FieldsAPI, Persona, ProgramConfig, UserId, WorkflowItem } from '@cibo/core'
import { Label, usePaginatedFields } from '@cibo/landmanager'
import { Loading, useAuth } from '@cibo/profile'
import { useProgramStats, usePrograms, useWorkflows } from '@cibo/programs'
import { DataGridPro } from '@cibo/ui'
import { Stack, Typography } from '@mui/material'
import { DataGridProProps, GridColumns, GridRenderCellParams } from '@mui/x-data-grid-pro'
import { useQueries } from '@tanstack/react-query'
import { Asset } from 'contentful'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { fieldCountsToColor } from 'utils'

export interface ProgramOpportunityListItem
  extends Pick<ProgramConfig, 'rewardTokens' | 'earned' | 'opportunities'> {
  id: string
  name: string
  logo: Asset
  saturation: number
  existingEnrollment?: WorkflowItem
  counts: {
    available: number
    total: number
  }
}

export const ProgramOpportunityList = ({
  onRowClick,
  personal,
  persona,
  rulesVersion,
}: Pick<DataGridProProps<ProgramOpportunityListItem>, 'onRowClick'> & {
  personal?: boolean
  persona?: Persona
  userId?: UserId
  rulesVersion?: 'v1' | 'v2'
}) => {
  const { t } = useTranslation('impact/components/ProgramOpportunityList')
  const { userId } = useAuth()
  const programs = usePrograms()
  const stats = useProgramStats({})
  const workflows = useWorkflows(0, 1)
  // const { acres } = useFigures()
  const totalFields = usePaginatedFields(0, 1)
  const fieldQueries = useQueries({
    queries:
      programs.data?.map(p => ({
        queryKey: ['fieldCount', p.programId],
        queryFn: () =>
          FieldsAPI.paginatedFields({
            limit: 0,
            availableForWorkflow: { programId: p.programId },
            persona,
          }),
        staleTime: 1,
      })) || [],
  })

  const columns = useMemo(() => {
    return [
      {
        field: 'name',
        renderCell: ({ row, value }: GridRenderCellParams<ProgramOpportunityListItem>) => {
          return (
            <Stack direction="row" alignItems="center" spacing={1}>
              <img
                style={{ height: '50px', width: '50px', objectFit: 'contain' }}
                src={`${row.logo.fields.file.url}`}
                srcSet={`${row.logo.fields.file.url}?w=50, ${row.logo.fields.file.url}?w=100 2x, ${row.logo.fields.file.url}?w=200 3x`}
                alt={row.logo.fields.description}
                loading="lazy"
              />
              <Stack>
                <Typography variant="body2">{value}</Typography>
                {!!row.rewardTokens && (
                  <Typography variant="caption">
                    {row.rewardTokens.map((context: string) => t('token', { context })).join(', ')}
                  </Typography>
                )}
              </Stack>
            </Stack>
          )
        },
        flex: 4,
      },
      {
        field: 'existingEnrollment',
        flex: 1.25,
        renderCell: ({ value, row }: GridRenderCellParams<WorkflowItem>) => {
          return !!value ? (
            <Label
              color={fieldCountsToColor(value.fieldCounts)}
              label={t('editExistingEnrollment')}
              size="small"
              // @ts-ignore this is saying it needs three args but it doesn't!
              onClick={() => onRowClick({ row })}
            />
          ) : (
            ''
          )
        },
      },
      {
        field: 'counts',
        flex: 1,
        align: 'right',
        renderCell: ({ value }: GridRenderCellParams<{ total?: number; available?: number }>) => (
          <Typography variant="body2">
            {t('counts', {
              total: value?.total,
              available: value?.available,
              count: value?.available,
            })}
          </Typography>
        ),
      },
      //@todo bring this back at some point
      // {
      //   field: 'opportunities',
      //   flex: 1,
      //   align: 'right',
      //   renderCell: personal
      //     ? ({
      //         value,
      //         row,
      //       }: GridRenderCellParams<RewardOpportunity[], ProgramOpportunityListItem>) => (
      //         <PersonalOpportunities userId={userId} programId={row.id} />
      //       )
      //     : ({ value = [] }: GridRenderCellParams<RewardOpportunity[]>) => (
      //         <Stack textAlign="right">
      //           <Typography variant="body2">
      //             {!!value[0]?.reward[0]
      //               ? t('reward', {
      //                   context: value[0]?.reward[0]?.token,
      //                   count: value[0]?.reward[0]?.amount,
      //                 })
      //               : '--'}
      //           </Typography>
      //           <Typography variant="caption">
      //             {!!value[0]?.reward[0]
      //               ? t('reward', {
      //                   context: value[0]?.reward[1]?.token,
      //                   count: value[0]?.reward[1]?.amount,
      //                 })
      //               : '--'}
      //           </Typography>
      //         </Stack>
      //       ),
      // },
    ] as GridColumns<ProgramOpportunityListItem>
  }, [programs.data, personal, userId])

  const rows = useMemo(() => {
    if (!!programs.data && stats.data) {
      //join programs and stats
      return programs.data
        .filter(a => a.rulesVersion === rulesVersion)
        .map(p => {
          const stat = stats.data.programs?.find(s => p.programId === s.programId)
          return {
            id: p.programId,
            name: p.program.content.fields.title,
            logo: p.program.content.fields.logo,
            earned: p.program.earned,
            rewardTokens: p.program.rewardTokens,
            saturation: !!stat?.eligible?.acres ? stat.committed?.acres / stat.eligible.acres : 0,
            opportunities: p.program.opportunities,
            rulesVersion: p.program.rulesVersion,
            counts: {
              available: fieldQueries.find(
                ({ data }) => data?.params?.availableForWorkflow?.programId === p.programId
              )?.data?.numAvailable,
              total: totalFields.data?.numAvailable,
            },
            existingEnrollment: personal
              ? workflows.data?.items.find(
                  w => w.programId === p.programId && w.ownedBy.userId === userId
                )
              : undefined,
          } as ProgramOpportunityListItem
        })
    }
  }, [
    programs.isLoading,
    stats.isLoading,
    personal,
    workflows.isLoading,
    fieldQueries.every(({ isFetched }) => isFetched),
    totalFields.isLoading,
  ])

  if (fieldQueries.some(({ isLoading }) => isLoading)) {
    return <Loading />
  }

  return (
    <DataGridPro
      //@ts-ignore the column model expects a POJO, but we provide a class ¯\_(ツ)_/¯
      columns={columns}
      rows={rows || []}
      loading={
        programs.isLoading ||
        stats.isLoading ||
        workflows.isLoading ||
        fieldQueries.some(({ isLoading }) => isLoading)
      }
      getRowClassName={({ row: { counts } }) => (counts?.available === 0 ? 'disabled' : '')}
      onRowClick={(p, e, d) => !!onRowClick && p.row.counts?.available !== 0 && onRowClick(p, e, d)}
      headerHeight={0}
      disableSelectionOnClick
      hideBorders
      hideCellFocus
      hideFooter
      autoHeight
    />
  )
}
